import {
	queryOptions,
	useQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import {
	getChannel,
	getChannelMembers,
	getChannels,
	getGroup,
	getKnownTeams,
	getKnownTeamTypes,
	getTeam,
	getTeamMemberCount,
	getTeamMembers,
	getTeamPhoto,
	listTeams,
} from "./api";

export const knownTeamTypesQueryOptions = () =>
	queryOptions({
		queryKey: ["teamsAdministration", "types"],
		queryFn: getKnownTeamTypes,
	});
export const knownTeamsQueryOptions = () =>
	queryOptions({
		queryKey: ["teamsAdministration", "knownTeams"],
		queryFn: getKnownTeams,
	});
export function useKnownTeamTypes() {
	return useQuery(knownTeamTypesQueryOptions());
}
export function useKnownTeams() {
	return useQuery(knownTeamsQueryOptions());
}

export const teamsAdministrationTeamsQueryOptions = (includeDetails = false) =>
	queryOptions({
		queryKey: ["teamsAdministration", "teams", { includeDetails }],
		queryFn: () => listTeams(includeDetails),
	});

export function useTeamsAdministrationTeams(includeDetails = false) {
	return useQuery(teamsAdministrationTeamsQueryOptions(includeDetails));
}

export function useTeamsAdministrationTeamsSuspense(includeDetails = false) {
	return useSuspenseQuery(teamsAdministrationTeamsQueryOptions(includeDetails));
}

export const teamsAdministrationTeamQueryOptions = (teamId: string) =>
	queryOptions({
		queryKey: ["teamsAdministration", "team", teamId],
		queryFn: () => getTeam(teamId),
		enabled: teamId.length > 0,
	});

export function useTeamsAdministrationTeam(teamId: string) {
	return useQuery(teamsAdministrationTeamQueryOptions(teamId));
}

export function useTeamsAdministrationTeamSuspense(teamId: string) {
	return useSuspenseQuery(teamsAdministrationTeamQueryOptions(teamId));
}

export const teamsAdministrationGroupQueryOptions = (groupId: string) =>
	queryOptions({
		queryKey: ["teamsAdministration", "group", groupId],
		queryFn: () => getGroup(groupId),
		enabled: groupId.length > 0,
	});

export function useTeamsAdministrationGroup(groupId: string) {
	return useQuery(teamsAdministrationGroupQueryOptions(groupId));
}

export function useTeamsAdministrationGroupSuspense(groupId: string) {
	return useSuspenseQuery(teamsAdministrationGroupQueryOptions(groupId));
}

export const teamsAdministrationChannelsQueryOptions = (teamId: string) =>
	queryOptions({
		queryKey: ["teamsAdministration", "team", teamId, "channels"],
		queryFn: () => getChannels(teamId),
		enabled: teamId.length > 0,
	});

export function useTeamsAdministrationChannels(teamId: string) {
	return useQuery(teamsAdministrationChannelsQueryOptions(teamId));
}

export function useTeamsAdministrationChannelsSuspense(teamId: string) {
	return useSuspenseQuery(teamsAdministrationChannelsQueryOptions(teamId));
}

export const teamsAdministrationChannelQueryOptions = (
	teamId: string,
	channelId: string,
) =>
	queryOptions({
		queryKey: ["teamsAdministration", "team", teamId, "channel", channelId],
		queryFn: () => getChannel(teamId, channelId),
		enabled: teamId.length > 0 && channelId.length > 0,
	});

export function useTeamsAdministrationChannel(
	teamId: string,
	channelId: string,
) {
	return useQuery(teamsAdministrationChannelQueryOptions(teamId, channelId));
}

export function useTeamsAdministrationChannelSuspense(
	teamId: string,
	channelId: string,
) {
	return useSuspenseQuery(
		teamsAdministrationChannelQueryOptions(teamId, channelId),
	);
}

export const teamsAdministrationTeamMembersQueryOptions = (teamId: string) =>
	queryOptions({
		queryKey: ["teamsAdministration", "team", teamId, "members"],
		queryFn: () => getTeamMembers(teamId),
		enabled: teamId.length > 0,
	});

export function useTeamsAdministrationTeamMembers(teamId: string) {
	return useQuery(teamsAdministrationTeamMembersQueryOptions(teamId));
}

export function useTeamsAdministrationTeamMembersSuspense(teamId: string) {
	return useSuspenseQuery(teamsAdministrationTeamMembersQueryOptions(teamId));
}

export const teamsAdministrationTeamMemberCountQueryOptions = (
	teamId: string,
) =>
	queryOptions({
		queryKey: ["teamsAdministration", "team", teamId, "memberCount"],
		queryFn: () => getTeamMemberCount(teamId),
		enabled: teamId.length > 0,
		staleTime: 5 * 60 * 1000,
	});

export function useTeamsAdministrationTeamMemberCount(teamId: string) {
	return useQuery(teamsAdministrationTeamMemberCountQueryOptions(teamId));
}

export const teamsAdministrationChannelMembersQueryOptions = (
	teamId: string,
	channelId: string,
) =>
	queryOptions({
		queryKey: [
			"teamsAdministration",
			"team",
			teamId,
			"channel",
			channelId,
			"members",
		],
		queryFn: () => getChannelMembers(teamId, channelId),
		enabled: teamId.length > 0 && channelId.length > 0,
	});

export function useTeamsAdministrationChannelMembers(
	teamId: string,
	channelId: string,
) {
	return useQuery(
		teamsAdministrationChannelMembersQueryOptions(teamId, channelId),
	);
}

export function useTeamsAdministrationChannelMembersSuspense(
	teamId: string,
	channelId: string,
) {
	return useSuspenseQuery(
		teamsAdministrationChannelMembersQueryOptions(teamId, channelId),
	);
}

export const teamsAdministrationTeamPhotoQueryOptions = (teamId: string) =>
	queryOptions({
		queryKey: ["teamsAdministration", "team", teamId, "photo"],
		queryFn: () => getTeamPhoto(teamId),
		enabled: teamId.length > 0,
	});

export function useTeamsAdministrationTeamPhoto(teamId: string) {
	return useQuery(teamsAdministrationTeamPhotoQueryOptions(teamId));
}

export function useTeamsAdministrationTeamPhotoSuspense(teamId: string) {
	return useSuspenseQuery(teamsAdministrationTeamPhotoQueryOptions(teamId));
}
